import type { ApiResponse } from 'shared/api/responses';
import { api, nullOn404, on404 } from 'shared/api/client';
import { requireValue } from 'shared/lib/assert';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { keepPreviousData, useMutation, useQuery, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  TaxonomyImportResult,
  PresentationTreeResponse,
  ConceptResponse,
  TaxonomySummary,
  AvailableTaxonomyFolder,
  CalculationGroupResponse,
  ExternalReferenceResponse,
  DimensionsForConceptResponse,
} from './types';

export const taxonomyQueryKeys = {
  all: 'TAXONOMIES',
  availableFolders: 'TAXONOMY_AVAILABLE_FOLDERS',
  presentationTree: 'PRESENTATION_TREE',
  concept: 'CONCEPT',
  activeTaxonomy: 'ACTIVE_TAXONOMY',
  calculations: 'CALCULATIONS',
  externalReferences: 'EXTERNAL_REFERENCES',
  dimensionsForConcept: 'DIMENSIONS_FOR_CONCEPT',
  conceptsByIds: 'CONCEPTS_BY_IDS',
  taxonomyLanguages: 'TAXONOMY_LANGUAGES',
} as const;

export function useTaxonomyLanguages(documentSuperId?: number, taxonomyId?: string): UseQueryResult<string[]> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.taxonomyLanguages, documentSuperId, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/taxonomy/languages', {
        params: {
          query: {
            documentSuperId: String(requireValue(documentSuperId, 'useTaxonomyLanguages ran without a documentSuperId')),
            taxonomyId: requireValue(taxonomyId, 'useTaxonomyLanguages ran without a taxonomyId'),
          },
        },
      });
      return res?.data ?? [];
    },

    enabled: !!documentSuperId && !!taxonomyId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function usePresentationTree(documentSuperId?: number, taxonomyId?: string): UseQueryResult<PresentationTreeResponse | null> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.presentationTree, documentSuperId, taxonomyId],

    queryFn: () =>
      api
        .GET('/taxonomy/presentation-tree', {
          params: { query: { documentSuperId: String(documentSuperId), ...(taxonomyId ? { taxonomyId } : {}) } },
        })
        .then((res) => res.data as PresentationTreeResponse)
        .catch(nullOn404),

    enabled: !!documentSuperId,

    // 5 minutes
    staleTime: 5 * 60 * 1000,

    // 30 minutes
    gcTime: 30 * 60 * 1000,
  });
}

export function useConcept(conceptName: string | null, documentSuperId?: number): UseQueryResult<ConceptResponse | null> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.concept, conceptName, documentSuperId],

    queryFn: async () => {
      if (!conceptName || !documentSuperId) return null;

      return api
        .GET('/taxonomy/concepts', {
          params: { query: { name: conceptName, documentSuperId: String(documentSuperId) } },
        })
        .then((res) => res.data)
        .catch(nullOn404);
    },

    enabled: !!conceptName && !!documentSuperId,

    // 5 minutes
    staleTime: 5 * 60 * 1000,

    // 30 minutes
    gcTime: 30 * 60 * 1000,
  });
}

export function useImportTaxonomy(): UseMutationResult<TaxonomyImportResult, unknown, string> {
  return useMutation({
    mutationFn: async (folder: string) => {
      const res = await api.POST('/taxonomy/import', { body: { folder } });
      return res?.data;
    },

    onError: ServerErrorHandler,
  });
}

export function useAvailableTaxonomyFolders(): UseQueryResult<AvailableTaxonomyFolder[]> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.availableFolders],

    queryFn: async () => {
      const res = await api.GET('/taxonomy/available', {});
      return res?.data || [];
    },

    staleTime: 30 * 1000,
  });
}

export function useImportedTaxonomies(): UseQueryResult<TaxonomySummary[]> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.all],

    queryFn: () =>
      api
        .GET('/taxonomy/', {})
        .then((res) => res.data)
        .catch(on404([])),

    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useActiveTaxonomy(documentId: string) {
  return useQuery({
    queryKey: [taxonomyQueryKeys.activeTaxonomy, documentId],

    queryFn: () =>
      api
        .GET('/taxonomy/active', { params: { query: { documentId } } })
        .then((res) => res.data)
        .catch(nullOn404),

    enabled: !!documentId,
  });
}

export function useExternalReferences(documentSuperId?: number, conceptName?: string | null): UseQueryResult<ExternalReferenceResponse[] | null> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.externalReferences, documentSuperId, conceptName],

    queryFn: async () => {
      if (!documentSuperId || !conceptName) return null;

      return api
        .GET('/taxonomy/external-references', {
          params: { query: { documentSuperId: String(documentSuperId), conceptName } },
        })
        .then((res) => res.data)
        .catch(nullOn404);
    },

    enabled: !!documentSuperId && !!conceptName,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export type ConceptByIdsItem = ApiResponse<'/taxonomy/concepts/batch', 'post'>[number];

export function useConceptsByIds(conceptIds: string[] | undefined): UseQueryResult<ConceptByIdsItem[]> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.conceptsByIds, [...(conceptIds ?? [])].sort()],

    queryFn: async () => {
      if (!conceptIds || conceptIds.length === 0) return [];
      const res = await api.POST('/taxonomy/concepts/batch', { body: { conceptIds } });
      return res?.data ?? [];
    },

    enabled: !!conceptIds && conceptIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useDimensionsForConcept(documentSuperId?: number, conceptName?: string | null): UseQueryResult<DimensionsForConceptResponse | null> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.dimensionsForConcept, documentSuperId, conceptName],

    queryFn: async () => {
      if (!documentSuperId || !conceptName) return null;
      return (
        api
          .GET('/taxonomy/dimensions-for-concept', {
            params: { query: { documentSuperId: String(documentSuperId), conceptName } },
          })
          .then((res) => res.data as DimensionsForConceptResponse)
          // Concept not in any dimensional network — empty result, not an error
          .catch(nullOn404)
      );
    },

    enabled: !!documentSuperId && !!conceptName,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useCalculations(
  documentSuperId?: number,
  conceptName?: string | null,
  contextId?: string,
  lang?: string,
  viewerTagId?: string
): UseQueryResult<CalculationGroupResponse[] | null> {
  return useQuery({
    queryKey: [taxonomyQueryKeys.calculations, documentSuperId, conceptName, contextId, lang, viewerTagId],

    queryFn: async () => {
      if (!documentSuperId || !conceptName) return null;

      return (
        api
          .GET('/taxonomy/calculations', {
            params: {
              query: {
                documentSuperId: String(documentSuperId),
                conceptName,
                ...(contextId ? { contextId } : {}),
                ...(lang ? { lang } : {}),
                ...(viewerTagId ? { viewerTagId } : {}),
              },
            },
          })
          // 204 (no calculations) is typed as "" by the contract — same null as a 404.
          .then((res) => res.data || null)
          .catch(nullOn404)
      );
    },

    enabled: !!documentSuperId && !!conceptName,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
